import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { EntityType } from '@/types/api';
import {
  isPendingSyncStuck,
  pendingSyncRefetchInterval,
  resetPendingSyncPolling,
} from '@/utils/pendingSyncPolling';

const fetcherMap: Record<EntityType, (restaurantId: number) => Promise<any[]>> = {
  menuCategories: (id) => apiClient.getSyncedMenuCategories(id),
  menuGroups: (id) => apiClient.getSyncedMenuGroups(id),
  menuItems: (id) => apiClient.getMenuItems(id),
  taxes: (id) => apiClient.getSyncedTaxes(id),
  discounts: (id) => apiClient.getSyncedDiscounts(id),
  orderTypes: (id) => apiClient.getSyncedOrderTypes(id),
  customPayments: (id) => apiClient.getSyncedCustomPayments(id),
  cookingInstructions: (id) => apiClient.getSyncedCookingInstructions(id),
  kitchenDisplayOrders: (id) => apiClient.getSyncedKitchenDisplayOrders(id),
  printerGroups: (id) => apiClient.getSyncedPrinterGroups(id),
  accountSalesAreas: (id) => apiClient.getSyncedAccountSalesAreas(id),
  askNextReasons: (id) => apiClient.getSyncedAskNextReasons(id),
  languages: (id) => apiClient.getSyncedLanguages(id),
  shifts: (id) => apiClient.getSyncedShifts(id),
};

export function useEntityData(entityType: EntityType, restaurantId: number) {
  const trackingKey = `${entityType}:${restaurantId}`;

  const query = useQuery({
    queryKey: ['synced-entity', entityType, restaurantId],
    queryFn: () => fetcherMap[entityType](restaurantId),
    enabled: !!restaurantId,
    // Poll every 3s while any item has a pending sync status, capped at
    // 2 minutes so a permanently stuck row doesn't poll forever.
    refetchInterval: pendingSyncRefetchInterval(trackingKey),
  });

  const isStuck = isPendingSyncStuck(trackingKey, query.data as Record<string, unknown>[] | undefined);

  return { ...query, isStuck };
}

// CRUD fetcher maps for entities that support create/update/delete
const createFetchers: Partial<Record<EntityType, (restaurantId: number, data: any) => Promise<any>>> = {
  taxes: (id, data) => apiClient.createSyncedTax(id, data),
  languages: (id, data) => apiClient.createSyncedLanguage(id, data),
  menuCategories: (id, data) => apiClient.createSyncedMenuCategory(id, data),
  cookingInstructions: (id, data) => apiClient.createSyncedCookingInstruction(id, data),
  menuGroups: (id, data) => apiClient.createSyncedMenuGroup(id, data),
  menuItems: (id, data) => apiClient.createMenuItem(id, data),
  customPayments: (id, data) => apiClient.createSyncedCustomPayment(id, data),
  kitchenDisplayOrders: (id, data) => apiClient.createSyncedKitchenDisplayOrder(id, data),
  accountSalesAreas: (id, data) => apiClient.createSyncedAccountSalesArea(id, data),
  askNextReasons: (id, data) => apiClient.createSyncedAskNextReason(id, data),
  discounts: (id, data) => apiClient.createSyncedDiscount(id, data),
  orderTypes: (id, data) => apiClient.createSyncedOrderType(id, data),
  printerGroups: (id, data) => apiClient.createSyncedPrinterGroup(id, data),
  shifts: (id, data) => apiClient.createSyncedShift(id, data),
};

const updateFetchers: Partial<Record<EntityType, (restaurantId: number, id: number, data: any) => Promise<any>>> = {
  taxes: (rid, id, data) => apiClient.updateSyncedTax(rid, id, data),
  languages: (rid, id, data) => apiClient.updateSyncedLanguage(rid, id, data),
  menuCategories: (rid, id, data) => apiClient.updateSyncedMenuCategory(rid, id, data),
  cookingInstructions: (rid, id, data) => apiClient.updateSyncedCookingInstruction(rid, id, data),
  menuGroups: (rid, id, data) => apiClient.updateSyncedMenuGroup(rid, id, data),
  menuItems: (rid, id, data) => apiClient.updateMenuItem(rid, id, data),
  customPayments: (rid, id, data) => apiClient.updateSyncedCustomPayment(rid, id, data),
  kitchenDisplayOrders: (rid, id, data) => apiClient.updateSyncedKitchenDisplayOrder(rid, id, data),
  accountSalesAreas: (rid, id, data) => apiClient.updateSyncedAccountSalesArea(rid, id, data),
  askNextReasons: (rid, id, data) => apiClient.updateSyncedAskNextReason(rid, id, data),
  discounts: (rid, id, data) => apiClient.updateSyncedDiscount(rid, id, data),
  orderTypes: (rid, id, data) => apiClient.updateSyncedOrderType(rid, id, data),
  printerGroups: (rid, id, data) => apiClient.updateSyncedPrinterGroup(rid, id, data),
  shifts: (rid, id, data) => apiClient.updateSyncedShift(rid, id, data),
};

const deleteFetchers: Partial<Record<EntityType, (restaurantId: number, id: number) => Promise<void>>> = {
  taxes: (rid, id) => apiClient.deleteSyncedTax(rid, id),
  languages: (rid, id) => apiClient.deleteSyncedLanguage(rid, id),
  menuCategories: (rid, id) => apiClient.deleteSyncedMenuCategory(rid, id),
  cookingInstructions: (rid, id) => apiClient.deleteSyncedCookingInstruction(rid, id),
  menuGroups: (rid, id) => apiClient.deleteSyncedMenuGroup(rid, id),
  menuItems: (rid, id) => apiClient.deleteMenuItem(rid, id),
  customPayments: (rid, id) => apiClient.deleteSyncedCustomPayment(rid, id),
  kitchenDisplayOrders: (rid, id) => apiClient.deleteSyncedKitchenDisplayOrder(rid, id),
  accountSalesAreas: (rid, id) => apiClient.deleteSyncedAccountSalesArea(rid, id),
  askNextReasons: (rid, id) => apiClient.deleteSyncedAskNextReason(rid, id),
  discounts: (rid, id) => apiClient.deleteSyncedDiscount(rid, id),
  orderTypes: (rid, id) => apiClient.deleteSyncedOrderType(rid, id),
  printerGroups: (rid, id) => apiClient.deleteSyncedPrinterGroup(rid, id),
  shifts: (rid, id) => apiClient.deleteSyncedShift(rid, id),
};

export function useEntityMutations(entityType: EntityType, restaurantId: number) {
  const queryClient = useQueryClient();
  const queryKey = ['synced-entity', entityType, restaurantId];
  const trackingKey = `${entityType}:${restaurantId}`;

  // A change just landed for this entity — restart its pending-sync poll budget
  // (see resetPendingSyncPolling) and let anyone watching draft counts know too,
  // since a create/update/delete here may have just staged or drained a draft row.
  const afterMutation = () => {
    resetPendingSyncPolling(trackingKey);
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['draftStatus'] });
  };

  const createMutation = useMutation({
    mutationFn: (data: any) => {
      const fn = createFetchers[entityType];
      if (!fn) throw new Error(`Create not supported for ${entityType}`);
      return fn(restaurantId, data);
    },
    onSuccess: afterMutation,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => {
      const fn = updateFetchers[entityType];
      if (!fn) throw new Error(`Update not supported for ${entityType}`);
      return fn(restaurantId, id, data);
    },
    onSuccess: afterMutation,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => {
      const fn = deleteFetchers[entityType];
      if (!fn) throw new Error(`Delete not supported for ${entityType}`);
      return fn(restaurantId, id);
    },
    onSuccess: afterMutation,
  });

  const hasCrud = !!createFetchers[entityType];

  return { createMutation, updateMutation, deleteMutation, hasCrud };
}
